import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { CheckCircle2, Clock, XCircle, MessageCircle } from "lucide-react";
import { orderQuery } from "@/lib/orders.functions";
import { SHIPPING_FALLBACK_ID } from "@/lib/mercadopago.functions";
import { formatBRL, whatsappLink } from "@/lib/format";
import { useStoreSettings } from "@/lib/settings.functions";

export const Route = createFileRoute("/pedido/$id")({
  loader: async ({ context, params }) => {
    const order = await context.queryClient.ensureQueryData(orderQuery(params.id));
    if (!order) throw notFound();
    return order;
  },
  head: () => ({
    meta: [
      { title: "Pedido recebido — Loja EG Elétrica" },
      { name: "description", content: "Seu pedido foi recebido com sucesso." },
      { property: "og:title", content: "Pedido recebido — Loja EG Elétrica" },
      { property: "og:description", content: "Seu pedido foi recebido com sucesso." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: PedidoPage,
});

const STATUS_LABEL: Record<string, string> = {
  pending: "Aguardando pagamento",
  paid: "Pagamento confirmado",
  processing: "Em processamento",
  shipped: "Enviado",
  delivered: "Entregue",
  cancelled: "Cancelado",
  refunded: "Reembolsado",
};

function PedidoPage() {
  const { id } = Route.useParams();
  const { data: order } = useSuspenseQuery({
    ...orderQuery(id),
    refetchInterval: (query) => (query.state.data?.status === "pending" ? 3000 : false),
  });
  const settings = useStoreSettings();

  if (!order) return null;

  const isRejected = order.status === "cancelled";
  const isPaid = !isRejected && order.status !== "pending";

  return (
    <div className="mx-auto max-w-2xl px-4 py-16">
      <div className="rounded-lg bg-white border border-border p-8 sm:p-12 text-center">
        {isRejected ? (
          <XCircle className="mx-auto h-16 w-16 text-red-600" />
        ) : isPaid ? (
          <CheckCircle2 className="mx-auto h-16 w-16 text-green-600" />
        ) : (
          <Clock className="mx-auto h-16 w-16 text-eg-gold" />
        )}
        <h1 className="mt-4 font-display text-2xl sm:text-3xl font-bold text-eg-dark">
          Pedido #{order.order_number}
        </h1>
        <p className="mt-2 text-sm font-semibold text-eg-dark">
          Status: {STATUS_LABEL[order.status] ?? order.status}
        </p>
        <p className="mt-3 text-muted-foreground">
          {isRejected
            ? "O pagamento não foi aprovado. Fale com a gente pelo WhatsApp se precisar de ajuda ou quiser tentar novamente."
            : order.status === "pending"
              ? "Estamos aguardando a confirmação do pagamento pelo Mercado Pago. Isso costuma levar poucos instantes."
              : "Seu pagamento foi confirmado! Acompanhe as próximas etapas pelo WhatsApp."}
        </p>
        <p className="mt-1 text-sm text-muted-foreground">
          Total: <span className="font-semibold text-eg-dark">{formatBRL(order.total)}</span>
        </p>

        {order.shipping_service_id === SHIPPING_FALLBACK_ID && (
          <p className="mt-4 rounded-md bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800">
            O frete deste pedido ainda não foi calculado — vamos combinar o valor e a forma de envio
            com você pelo WhatsApp.
          </p>
        )}

        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          <a
            href={whatsappLink(
              settings.whatsapp_number,
              `Olá! Acabei de finalizar o pedido #${order.order_number} e gostaria de confirmar.`,
            )}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 rounded-md bg-green-600 px-6 py-3 font-semibold text-white hover:bg-green-700 transition-colors"
          >
            <MessageCircle className="h-5 w-5" />
            Falar no WhatsApp
          </a>
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md border-2 border-eg-dark px-6 py-3 font-semibold text-eg-dark hover:bg-eg-dark hover:text-white transition-colors"
          >
            Continuar Comprando
          </Link>
        </div>
      </div>
    </div>
  );
}
